import React, { createContext, FC, ReactNode } from 'react';

import { MeQuery, useMeQuery, User } from '../types/api.graphql';

type Nullable<T> = T | null;

interface SessionContextProps {
  me: Nullable<Omit<User, 'shadowEntity'>>;
  hasOrgWithPaidSubscription: boolean;
  /**
   * This will determine if the current admin user logged in is currently shadowing another user or organization.
   */
  shadowEntity: MeQuery['me']['shadowEntity'];
}

interface SessionProviderProps {
  children: ReactNode;
}

const SessionContext = createContext<SessionContextProps>({
  me: null,
  hasOrgWithPaidSubscription: false,
  shadowEntity: null,
});

export const SessionProvider: FC<SessionProviderProps> = ({ children }) => {
  const { data } = useMeQuery();

  const hasOrgWithPaidSubscription = data?.me?.organizations?.some((org) => org?.paidSubscription);

  const shadowEntity = data?.me?.shadowEntity;

  return (
    <SessionContext.Provider
      value={{
        me: data?.me || null,
        hasOrgWithPaidSubscription,
        shadowEntity,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSessionContext: () => SessionContextProps = () => {
  return React.useContext(SessionContext);
};
